import React from 'react';
import { Card, CardHeader, CardBody, Spinner, Col } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import Avatar from 'react-avatar';
import { fetchEmployeeProfilePicture } from '../../../store/apis/employeeService';
import '../index.scss';
import { getAuthUser } from '../../../utils/auth.util';
import { getCompanyCode } from '../../../config/axios.config';
import * as icons from '../../../assets';

function ProfileInfo({ data, isLoading }) {
  const { id, employeeId } = getAuthUser();
  const companyCode = getCompanyCode();

  const employeeData = data?.[0];
  const { data: profilePicture } = useQuery({
    queryKey: ['profileInfoPicture'],
    queryFn: () =>
      fetchEmployeeProfilePicture({
        userId: id,
        employeeId,
        companyCode,
      }),
  });

  return (
    <div className="profile-component h-100 d-flex flex-row gap-3 p-4">
      {/* Personal Details */}
      <Col>
        <Card className="profile-card">
          <CardHeader className="profile-card-header p-0 border-0">
            <div className="p-0 d-flex flex-column align-items-center justify-content-center">
              <div className="profile-bg" />
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '4px solid #fff',
                  borderRadius: '50%',
                  padding: '0px',
                  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
                }}
              >
                <Avatar
                  round
                  name={employeeData?.EmployeeName || 'Unknown'}
                  size="7.5rem"
                  color="#CFCBDC"
                  fgColor="#475467"
                  maxInitials={2}
                  src={profilePicture}
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                />
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="d-flex flex-row justify-content-center align-items-center gap-1">
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </div>
            ) : (
              <div className="d-flex flex-column gap-1">
                <div className="d-flex flex-column gap-2 text-center mb-3">
                  <span className="profile-name fs-3">
                    {employeeData?.EmployeeName || '-'}
                  </span>
                  <span className="profile-email">
                    {employeeData?.Email || '-'}
                  </span>
                </div>
                <div className="profile-info-separator mb-2" />
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Gender</span>
                  <span className="profile-value">
                    {employeeData?.Gender || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Date of Birth</span>
                  <span className="profile-value">
                    {employeeData?.DateofBirth || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Nationality</span>
                  <span className="profile-value">
                    {employeeData?.Nationality || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Religion</span>
                  <span className="profile-value">
                    {employeeData?.Religion || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Marital Status</span>
                  <span className="profile-value">
                    {employeeData?.MaritalStatus || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Blood Group</span>
                  <span className="profile-value">
                    {employeeData?.BloodGroup || '-'}
                  </span>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
      {/* Employment Information */}
      <Col>
        <Card className="profile-card">
          <CardHeader className="profile-card-header user-profile-header py-2">
            <div className="user-profile-header-icon">
              <img
                src={icons.iconsProfileOffice}
                alt="Profile Bank Icon"
                height="15px"
              />
            </div>
            <div>
              <span className="fw-semibold fs-5">Employment Information</span>
            </div>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="d-flex flex-row justify-content-center align-items-center gap-1">
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Hire Date</span>
                  <span className="profile-value">
                    {employeeData?.HireDate || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Entity</span>
                  <span className="profile-value">
                    {employeeData?.Entity || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Cost Center</span>
                  <span className="profile-value">
                    {employeeData?.CostCenter || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Department</span>
                  <span className="profile-value">
                    {employeeData?.Department || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Section</span>
                  <span className="profile-value">
                    {employeeData?.Section || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Classification</span>
                  <span className="profile-value">
                    {employeeData?.Classification || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Bonus Scheme</span>
                  <span className="profile-value">
                    {employeeData?.BonusScheme || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">PCM Scheme</span>
                  <span className="profile-value">
                    {employeeData?.PCM || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Designation</span>
                  <span className="profile-value">
                    {employeeData?.DesignationInt || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Designation (Ext)</span>
                  <span className="profile-value">
                    {employeeData?.DesignationExt || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Designation (Visa)</span>
                  <span className="profile-value">
                    {employeeData?.DesignationVisa || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Reporting To</span>
                  <span className="profile-value">
                    {employeeData?.ReportsTo || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Sponsor</span>
                  <span className="profile-value">
                    {employeeData?.Sponsor || '-'}
                  </span>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
      <Col>
        <div className="d-flex flex-column gap-3 h-100">
          {/* Ticket Entitlement */}
          <Card className="profile-card h-50">
            <CardHeader className="profile-card-header user-profile-header py-2">
              <div className="user-profile-header-icon">
                <img
                  src={icons.iconsProfileEntitlement}
                  alt="Ticket Entitlement Icon"
                  height="15px"
                />
              </div>
              <div>
                <span className="fw-semibold fs-5">Ticket Entitlement</span>
              </div>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <div className="d-flex flex-row justify-content-center align-items-center gap-1">
                  <Spinner color="dark" size="sm" />
                  <span className="ms-2">Loading...</span>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Entitled</span>
                    <span className="profile-value">
                      {employeeData?.TktEntitled || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Actual</span>
                    <span className="profile-value">
                      {employeeData?.TktActual || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Sector</span>
                    <span className="profile-value">
                      {employeeData?.Sector || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Class</span>
                    <span className="profile-value">
                      {employeeData?.TktClass || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Due On</span>
                    <span className="profile-value">
                      {employeeData?.Due || '-'}
                    </span>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
          {/* Vacation Entitlement */}
          <Card className="profile-card h-50">
            <CardHeader className="profile-card-header user-profile-header py-2">
              <div className="user-profile-header-icon">
                <img
                  src={icons.iconsProfileEntitlement}
                  alt="Vacation Entitlement Icon"
                  height="15px"
                />
              </div>
              <div>
                <span className="fw-semibold fs-5">Vacation Entitlement</span>
              </div>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <div className="d-flex flex-row justify-content-center align-items-center gap-1">
                  <Spinner color="dark" size="sm" />
                  <span className="ms-2">Loading...</span>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Entitled</span>
                    <span className="profile-value">
                      {employeeData?.VacEntitled || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Balance</span>
                    <span className="profile-value">
                      {employeeData?.VacBalance || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Last Used</span>
                    <span className="profile-value">
                      {employeeData?.VacLastUsed || '-'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <span className="profile-label">Carry Over</span>
                    <span className="profile-value">
                      {employeeData?.VacCarryOver || '-'}
                    </span>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </Col>
      {/* Bank Details */}
      <Col>
        <Card className="profile-card">
          <CardHeader className="profile-card-header user-profile-header py-2">
            <div className="user-profile-header-icon">
              <img
                src={icons.iconsProfileBank}
                alt="Profile Bank Icon"
                height="15px"
              />
            </div>
            <div>
              <span className="fw-semibold fs-5">Bank Details</span>
            </div>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="d-flex flex-row justify-content-center align-items-center gap-1">
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Bank Name</span>
                  <span className="profile-value">
                    {employeeData?.Bank || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Bank Branch</span>
                  <span className="profile-value">
                    {employeeData?.BankBranch || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Account Number</span>
                  <span className="profile-value">
                    {employeeData?.AccountNo || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Account Name</span>
                  <span className="profile-value">
                    {employeeData?.AccountName || '-'}
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="profile-label">Currency</span>
                  <span className="profile-value">
                    {employeeData?.PayCurrency || '-'}
                  </span>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}

export default ProfileInfo;
